import legislationServices from '@/services/legislation-services';
import { HammerIcon, HistoryIcon, LoaderIcon } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { LegislationVersionComparison } from '../legislation-version-comparison';
import { LegislationConnectedDecisionsDialog } from './legislation-connected-decisions-dialog';

// Issue #6694 (epic #6693): judikatúra-to-provision matching was DISABLED —
// it ranked by raw citation metadata (citation ≠ relevance), surfacing
// decisions that merely mention the § among many others. Re-enabled (#6698),
// gated to the curated editorial-holding corpus: the backend now only counts
// decrees promoted from the Zbierka R-decision overlay (an official R-label +
// právna veta), not every decree that happens to cite the §. The ÚS side
// stays out of scope until v3 extraction ships (#9720, #6696 — superseded).
// The version-history badge below is a separate feature, unaffected either way.
const JUDIKATURA_MATCHING_ENABLED: boolean = true;

interface LegislationDecisionHistoryProps {
  refIdWithId: string;
  top: number;
  rightOffset: number;
  iri: string;
  elementId: string;
}

function LegislationDecisionHistory({
  refIdWithId,
  top,
  rightOffset,
  iri,
  elementId,
}: LegislationDecisionHistoryProps) {
  const [dataLoaded, setDataLoaded] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [apiStatus, setApiStatus] = useState({
    history: { loading: false, count: 0 },
    hammer: { loading: false, count: 0 },
  });
  const containerRef = useRef<HTMLDivElement>(null);

  const [openDecisionDialog, setOpenDecisionDialog] = useState(false);
  const [refId, setRefId] = useState<string | null>(null);
  const [openVersionDialog, setOpenVersionDialog] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;
        setIsVisible(entry.isIntersecting);
        // Only trigger data loading if not already loaded
        if (entry.isIntersecting && !dataLoaded) {
          if (JUDIKATURA_MATCHING_ENABLED) handleHammerCount();
          setApiStatus((prev) => ({
            ...prev,
            history: { ...prev.history, loading: true },
          }));
          setDataLoaded(true);
        }
      },
      {
        root: null,
        rootMargin: '0px',
        threshold: 0.1,
      }
    );

    // Start observing the element
    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    // Cleanup observer on component unmount
    return () => {
      if (containerRef.current) {
        observer.unobserve(containerRef.current);
      }
    };
  }, [refIdWithId, dataLoaded]);

  const handleHammerCount = async () => {
    // Prevent multiple simultaneous calls
    if (apiStatus.hammer.loading) return;

    // Set loading state
    setApiStatus((prev) => ({
      ...prev,
      hammer: { ...prev.hammer, loading: true },
    }));
    try {
      const result =
        await legislationServices.getLegislationReferencedDecisionsCount(
          refIdWithId
        );
      // Update state with result
      setApiStatus((prev) => ({
        ...prev,
        hammer: {
          loading: false,
          count: result.data,
        },
      }));
    } catch {
      // Handle error
      setApiStatus((prev) => ({
        ...prev,
        hammer: { loading: false, count: 0 },
      }));
    }
  };

  const handleHistoryClick = () => {
    setOpenVersionDialog(true);
  };

  const handleHammerClick = () => {
    setRefId(refIdWithId);
    setOpenDecisionDialog(true);
  };

  // Handler for receiving version count from LegislationVersionComparison
  const handleVersionCountChange = (count: number) => {
    setApiStatus((prev) => ({
      ...prev,
      history: {
        loading: false,
        count: count,
      },
    }));
  };

  useEffect(() => {
    if (!openDecisionDialog) {
      setRefId(null);
    }
  }, [openDecisionDialog]);

  // Loading state - show when data is being fetched
  const isLoading = apiStatus.history.loading || apiStatus.hammer.loading;

  // Determine if we should show the buttons (only when data is loaded and count > threshold)
  const showHistoryButton =
    !apiStatus.history.loading && apiStatus.history.count > 2;
  const showHammerButton =
    JUDIKATURA_MATCHING_ENABLED &&
    !apiStatus.hammer.loading &&
    apiStatus.hammer.count > 0;
  const showContainer = isLoading || showHistoryButton || showHammerButton;

  // Don't render anything if not loading and no data
  if (!showContainer) {
    return (
      <div
        ref={containerRef}
        key={refIdWithId + top}
        data-section-id={elementId}
        className='absolute overflow-hidden'
        style={{ top, right: rightOffset - 40, width: 0, height: 0 }}
      >
        {/* Hidden container for intersection observer and data fetching */}
        <LegislationVersionComparison
          iri={iri}
          elementId={elementId}
          onVersionCountChange={handleVersionCountChange}
          isVisible={isVisible}
          open={openVersionDialog}
          setOpen={setOpenVersionDialog}
        />
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      key={refIdWithId + top}
      style={{ top, right: rightOffset - 40, zIndex: 10 }}
      data-section-id={elementId}
      className='inline-flex items-center gap-1 absolute'
    >
      {refId && openDecisionDialog && (
        <LegislationConnectedDecisionsDialog
          refId={refId}
          open={openDecisionDialog}
          setOpen={setOpenDecisionDialog}
        />
      )}

      {/* Loading state */}
      {isLoading && (
        <span className='inline-flex items-center text-content-muted'>
          <LoaderIcon className='w-4 h-4 animate-spin' />
        </span>
      )}

      {/* History section */}
      {!isLoading && showHistoryButton && (
        <button
          onClick={handleHistoryClick}
          className='inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-sm
            text-brand-action-muted hover:text-brand-tertiary hover:bg-brand-tertiary/10
            transition-colors cursor-pointer'
          title='Version history'
        >
          <HistoryIcon className='w-4 h-4' />
          <span className='font-medium'>
            {apiStatus.history.count > 99 ? '99+' : apiStatus.history.count}
          </span>
        </button>
      )}

      {/* Hidden LegislationVersionComparison for data fetching */}
      <LegislationVersionComparison
        iri={iri}
        elementId={elementId}
        onVersionCountChange={handleVersionCountChange}
        isVisible={isVisible}
        open={openVersionDialog}
        setOpen={setOpenVersionDialog}
      />

      {/* Hammer section */}
      {!isLoading && showHammerButton && (
        <button
          onClick={handleHammerClick}
          className='inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-sm
            text-brand-action-muted hover:text-brand-tertiary hover:bg-brand-tertiary/10
            transition-colors cursor-pointer'
          title='Court decisions'
        >
          <HammerIcon className='w-4 h-4' fill='currentColor' />
          <span className='font-medium'>
            {apiStatus.hammer.count > 99 ? '99+' : apiStatus.hammer.count}
          </span>
        </button>
      )}
    </div>
  );
}

export default LegislationDecisionHistory;
